import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import Layout, { Breadcrumbs } from "../components/Layout";
import Seo from "../components/Seo";
import { DREAM_CHART } from "../data/dreamChart";

const crumbs = [
  { name: "Home", path: "/" },
  { name: "Dream Number", path: "/dream-number/" },
];

export default function DreamNumber() {
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return DREAM_CHART;
    return DREAM_CHART.filter(
      (r) =>
        r.dream.toLowerCase().includes(term) ||
        r.direct.includes(term) ||
        String(r.sl) === term,
    );
  }, [q]);

  return (
    <Layout>
      <Seo
        title="Bhutan Morning Teer Dream Number Chart – Dream Numbers List"
        description="Bhutan Morning Teer dream number chart with Direct, House and Ending numbers for common dreams. Search your dream and find the associated numbers."
        path="/dream-number/"
        breadcrumb={crumbs}
      />
      <Breadcrumbs items={crumbs} />

      <div className="mx-auto max-w-3xl px-3 py-4">
        <h1 className="mb-1 text-2xl font-extrabold text-[#10161f] sm:text-3xl">
          Bhutan Morning Teer Dream Number
        </h1>
        <p className="mb-4 text-sm text-slate-600">
          Find the Direct, House and Ending numbers associated with your dream.
          Informational reference chart only.
        </p>

        <div className="relative mb-4">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            data-testid="dream-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search your dream (e.g. snake, dog, money)…"
            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-[#4a90d9]"
          />
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-300 shadow-sm">
          <table
            data-testid="dream-chart-table"
            className="w-full table-fixed border-collapse text-left"
          >
            <colgroup>
              <col className="w-[12%] sm:w-[9%]" />
              <col className="w-[38%] sm:w-[33%]" />
              <col className="w-[34%] sm:w-[42%]" />
              <col className="w-[8%]" />
              <col className="w-[8%]" />
            </colgroup>
            <thead>
              <tr className="bg-[#4a90d9] text-white">
                <th className="border border-[#3f7fc0] px-1.5 py-2 text-xs font-semibold sm:text-sm">
                  Sl.no
                </th>
                <th className="border border-[#3f7fc0] px-1.5 py-2 text-xs font-semibold sm:text-sm">
                  Dream
                </th>
                <th className="border border-[#3f7fc0] px-1.5 py-2 text-xs font-semibold sm:text-sm">
                  Direct
                </th>
                <th className="border border-[#3f7fc0] px-1 py-2 text-xs font-semibold sm:text-sm">
                  House
                </th>
                <th className="border border-[#3f7fc0] px-1 py-2 text-xs font-semibold sm:text-sm">
                  Ending
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    data-testid="dream-no-result"
                    className="px-3 py-8 text-center text-sm text-slate-500"
                  >
                    No dream found for "{q}". Try another word.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.sl} className="odd:bg-white even:bg-[#f4f8fd]">
                    <td className="break-words border border-slate-200 px-1.5 py-1.5 text-xs text-[#10161f] sm:text-sm">
                      {r.sl}
                    </td>
                    <td className="break-words border border-slate-200 px-1.5 py-1.5 text-xs font-medium text-[#10161f] sm:text-sm">
                      {r.dream}
                    </td>
                    <td className="break-words border border-slate-200 px-1.5 py-1.5 text-xs text-slate-700 sm:text-sm">
                      {r.direct || "-"}
                    </td>
                    <td className="break-words border border-slate-200 px-1 py-1.5 text-center text-xs font-semibold text-[#10161f] sm:text-sm">
                      {r.house || "-"}
                    </td>
                    <td className="break-words border border-slate-200 px-1 py-1.5 text-center text-xs font-semibold text-[#10161f] sm:text-sm">
                      {r.ending || "-"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-xs text-slate-500">
          Note: This dream number chart is provided for informational and
          entertainment purposes only and does not guarantee any result.
        </p>
      </div>
    </Layout>
  );
}
